import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isEmpty } from 'lodash';

import { useAuth } from 'contexts/auth';
import { useChat } from 'contexts/chat';
import { useViewport } from 'contexts/viewport';
import withResponsiveLayout from 'hoc/withResponsive';
import Mobile from 'parts/Header/mobile';
import Tablet from 'parts/Header/tablet';
import Desktop from 'parts/Header/desktop';
import { AlertDialog } from 'shell/components';

const getNavOptions = (path, role, hasUnreadMessages) => {
    return path === '/'
        ? [
              {
                  key: 'course-dashboard',
                  text: 'Courses',
                  link: '/',
                  icon: 'home',
                  badge: false,
              },
          ]
        : {
              student: [
                  {
                      key: 'dashboard',
                      text: 'Dashboard',
                      link: '/dashboard',
                      icon: 'dashboard',
                      badge: false,
                  },
                  {
                      key: 'tasks',
                      text: 'Tasks',
                      link: '/tasks',
                      icon: 'task',
                      badge: false,
                  },
                  {
                      key: 'message',
                      text: 'Messages',
                      link: '/messages',
                      icon: 'message',
                      badge: hasUnreadMessages,
                  },
                  {
                      key: 'more',
                      text: 'More',
                      link: '',
                      icon: 'hamburger',
                      badge: false,
                      options: [
                          { key: 'files', value: 'Shared Files' },
                          { key: 'activities', value: 'Team Activity' },
                          { key: 'peer-evaluation', value: 'Peer Evaluations' },
                      ],
                  },
              ],
              instructor: [
                  {
                      key: 'dashboard',
                      text: 'Overview',
                      link: '/overview',
                      icon: 'dashboard',
                      badge: false,
                  },
                  {
                      key: 'message',
                      text: 'Messages',
                      link: '/messages',
                      icon: 'message',
                      badge: hasUnreadMessages,
                  },
                  {
                      key: 'members',
                      text: 'People',
                      link: '/people',
                      icon: 'teams',
                      badge: false,
                  },
                  {
                      key: 'tasks',
                      text: 'Tasks',
                      link: '/task-manager',
                      icon: 'task',
                      badge: false,
                  },
                  {
                      key: 'more',
                      text: 'More',
                      link: '',
                      icon: 'hamburger',
                      badge: false,
                      options: [{ key: 'peer-evaluation', value: 'Evaluations' }],
                  },
              ],
          }[role];
};

const DROPDOWN_MENU_ITEMS_BY_ROLE = {
    student: {
        LTI: [
            { key: 'profile', value: 'Profile' },
            { key: 'tour', value: 'Getting Started' },
            { key: 'course-dashboard', value: 'Course Dashboard' },
        ],
        PASSWORD: [
            { key: 'profile', value: 'Profile' },
            { key: 'tour', value: 'Getting Started' },
            { key: 'switch-course', value: 'Switch Course' },
            { key: 'divider' },
        ],
    },
    instructor: {
        LTI: [
            { key: 'profile', value: 'Profile' },
            { key: 'course-dashboard', value: 'Course Dashboard' },
        ],
        PASSWORD: [
            { key: 'profile', value: 'Profile' },
            { key: 'switch-course', value: 'Switch Course' },
            { key: 'divider' },
        ],
    },
};

const getDropdownOptions = (isSignedIn, role) => {
    const isLTI = process.env.REACT_APP_AUTH === 'LTI';
    let universalMenus = [{ key: 'user' }, { key: 'divider' }];
    if (isLTI) {
        return [...universalMenus, ...(isSignedIn ? DROPDOWN_MENU_ITEMS_BY_ROLE[role].LTI : [])];
    }
    return [
        ...universalMenus,
        ...(isSignedIn ? DROPDOWN_MENU_ITEMS_BY_ROLE[role].PASSWORD : []),
        { key: 'logout', value: 'Log Out' },
    ];
};

const Header = () => {
    const location = useLocation();
    const { auth, logout, changeAccount } = useAuth();
    const { data } = useChat();
    const { width } = useViewport();

    const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);

    const hasUnreadMessages =
        data.reduce((acc, { unread_channels, unread_directs }) => {
            return acc + unread_channels + unread_directs;
        }, 0) > 0;

    const navOptions = getNavOptions(location.pathname, auth.role, hasUnreadMessages);
    const dropdownOptions = getDropdownOptions(auth.owner_account_id, auth.role);

    const props = {
        navOptions,
        dropdownOptions,
        logout: () => setIsAlertDialogOpen(true),
        ...(process.env.REACT_APP_AUTH === 'PASSWORD' && { changeAccount }),
    };

    const responsiveLayouts = {
        mobile: <Mobile {...props} />,
        tablet: <Tablet {...props} />,
        desktop: <Desktop {...props} />,
    };

    return isEmpty(auth) ? (
        <></>
    ) : (
        <>
            {withResponsiveLayout(width, responsiveLayouts)}
            <AlertDialog
                actionButtonText={'Log Out'}
                description={'Are you sure you would like to log out of Ensightful?'}
                message={'Logging Out?'}
                open={isAlertDialogOpen}
                variant={'destructive'}
                onAction={() => {
                    setIsAlertDialogOpen(false);
                    logout({ isExpired: false });
                }}
                onCancel={() => setIsAlertDialogOpen(false)}
                onClose={() => setIsAlertDialogOpen(false)}
            />
        </>
    );
};

export { Desktop as DesktopHeader, Tablet as TabletHeader, Mobile as MobileHeader };
export default Header;
